import React, { useCallback } from "react"
import { Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { ModusTextField } from "modus/components/form/text-field";
import { TextArea } from "modus/components/form/text-area";
import { 
  updateFormField, 
  validateProductName, 
  validateProductDescription 
} from "../../../../../store/actions/productFormActions";
import { RootState } from "../../../../../store/types";
import { PRODUCT_FORM_LIMITS } from "./constants";
import { useProductFormStyles } from "./ProductForm.style";

// Type for validation result
interface ValidationResult {
  valid: boolean;
  error: string | null;
}

export const ProductBasics: React.FC = () => {
    const classes = useProductFormStyles();
    const dispatch = useDispatch();
    
    // Get form data from Redux
    const { name, description, isEditing, product } = useSelector((state: RootState) => state.productForm);
    /** Disable description only when product is already public on the server, not for a draft public selection. */
    const isPublicPersisted =
      isEditing && (product?.visibility ?? '').toLowerCase() === 'public';
    const isDescriptionDisabled = isPublicPersisted;
    const nameDisabled = Boolean(isEditing && product);
    
    // Handle name change with validation — sole source of truth for name.valid (avoid setFieldValidation fighting ModusTextField)
    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const validation = validateProductName(value) as ValidationResult;
      
      dispatch(updateFormField('name', value, validation.valid, validation.error));
    }, [dispatch]);
    
    // Handle description change with validation
    const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const validation = validateProductDescription(value) as ValidationResult;
      
      dispatch(updateFormField('description', value, validation.valid, validation.error));
    }, [dispatch]);
    
    return (
        <>
        {/* Name Field */}
        <Box className={classes.formGroup} style={{ marginTop: 10 }}>
          <ModusTextField
            name="product-name"
            value={name.value}
            onChange={handleNameChange}
            validate={{
              required: true,
              requiredMessage: "Enter a product name",
              maxLength: PRODUCT_FORM_LIMITS.MAX_PRODUCT_NAME_LENGTH,
              allowSpace: true,
              pattern: '^[A-Za-z0-9 _-]+$',
              patternMessage: "Invalid Product Name. Only alpha-numerals, spaces, hyphen and underscore are allowed."
            }}
            placeholder="Product name"
            disabled={nameDisabled}
            autoFocus={!isEditing}
            label={<FormattedMessage defaultMessage="Name" />}
            required
            maxCount={PRODUCT_FORM_LIMITS.MAX_PRODUCT_NAME_LENGTH}
            labelStyle={
              isPublicPersisted && nameDisabled ? { color: "#464B52" } : undefined
            }
            error={!name.valid}
            helperText={nameDisabled ? undefined : (name.error || undefined)}
          />
        </Box>
      
      {/* Description Field */}
      <Box className={classes.formGroup} style={{ marginTop: 10 }}>
        <TextArea
          name="product-description"
          value={description.value}
          onChange={handleDescriptionChange}
          validate={{ maxLength: PRODUCT_FORM_LIMITS.MAX_PRODUCT_DESCRIPTION_LENGTH }}
          placeholder="Enter product description"
          disabled={isDescriptionDisabled}
          label={<FormattedMessage defaultMessage="Description" />}
          labelHint="(optional)"
          maxCount={PRODUCT_FORM_LIMITS.MAX_PRODUCT_DESCRIPTION_LENGTH}
          error={!description.valid}
          helperText={description.error ?? undefined}
        />
      </Box>
      </>
    )
}
